/**
 * Charts — Full TradingView-style workstation.
 * Candlestick + indicators overlay + oscillator panes + auto signal/pattern panel.
 * Shows empty chart with "No Data" message when symbol has no bars.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import {
  createChart,
  type IChartApi,
  ColorType,
  LineStyle,
  CrosshairMode,
  CandlestickSeries,
  LineSeries,
  HistogramSeries,
} from "lightweight-charts";

// ─── Types ───────────────────────────────────────────────────────────────────
// Intraday (1m/5m): time is Unix seconds (number)
// Daily (1D): time is {year, month, day} object
interface BarData { time: { year: number; month: number; day: number } | number; open: number; high: number; low: number; close: number; }
interface LineData { time: { year: number; month: number; day: number } | number; value: number; }
interface VolumeData { time: { year: number; month: number; day: number } | number; value: number; color: string; }
interface HistData { time: { year: number; month: number; day: number } | number; value: number; color: string; }

interface Signal { type: 'BUY' | 'SELL' | 'NEUTRAL'; indicator: string; reason: string; strength: number; date: string; }
interface Pattern {
  trend: 'BULLISH' | 'BEARISH' | 'SIDEWAYS';
  support: number; resistance: number;
  pattern: string; confidence: number; keyLevels: number[];
  candlePattern: string | null;
}
interface Indicators {
  sma20: LineData[]; sma50: LineData[]; ema12: LineData[]; ema26: LineData[];
  bb_upper: LineData[]; bb_middle: LineData[]; bb_lower: LineData[]; vwap: LineData[];
  rsi14: LineData[]; macd_line: LineData[]; macd_signal: LineData[]; macd_histogram: HistData[];
  stoch_k: LineData[]; stoch_d: LineData[]; atr14: LineData[];
  adx14: LineData[]; plusDI: LineData[]; minusDI: LineData[];
  obv: LineData[]; willr: LineData[]; cci20: LineData[];
}
interface ChartResponse {
  symbol: string; timeframe: string; barCount: number;
  dateRange: { from: string | null; to: string | null };
  livePrice: number | null;
  candles: BarData[]; volumes: VolumeData[];
  indicators: Indicators; signals: Signal[]; pattern: Pattern | null;
}
type Timeframe = '1m' | '5m' | '1D';

// ─── Indicator definitions ──────────────────────────────────────────────────
const INDICATORS: Record<string, { label: string; color: string; pane: 'main' | 'osc' | 'volume'; description: string }> = {
  sma20:        { label: 'SMA 20',       color: '#00cec9', pane: 'main',   description: 'Simple Moving Average 20' },
  sma50:        { label: 'SMA 50',       color: '#fdcb6e', pane: 'main',   description: 'Simple Moving Average 50' },
  ema12:        { label: 'EMA 12',       color: '#fd79a8', pane: 'main',   description: 'Exponential Moving Average 12' },
  ema26:        { label: 'EMA 26',       color: '#e17055', pane: 'main',   description: 'Exponential Moving Average 26' },
  bb_upper:     { label: 'BB Upper',    color: '#74b9ff80', pane: 'main', description: 'Bollinger Upper Band' },
  bb_middle:    { label: 'BB Middle',    color: '#74b9ff60', pane: 'main', description: 'Bollinger Middle Band' },
  bb_lower:     { label: 'BB Lower',    color: '#74b9ff80', pane: 'main', description: 'Bollinger Lower Band' },
  vwap:         { label: 'VWAP',         color: '#ffeaa7', pane: 'main',   description: 'Volume Weighted Average Price' },
  rsi14:        { label: 'RSI 14',       color: '#a29bfe', pane: 'osc',    description: 'Relative Strength Index' },
  macd_line:    { label: 'MACD',         color: '#0984e3', pane: 'osc',    description: 'MACD Line (12/26)' },
  macd_signal:  { label: 'MACD Signal',  color: '#e84393', pane: 'osc',    description: 'MACD Signal (9)' },
  macd_histogram: { label: 'MACD Hist',  color: '#00b894', pane: 'osc',    description: 'MACD Histogram' },
  stoch_k:      { label: 'Stoch %K',     color: '#6c5ce7', pane: 'osc',    description: 'Stochastic %K' },
  stoch_d:      { label: 'Stoch %D',     color: '#e17055', pane: 'osc',    description: 'Stochastic %D' },
  atr14:        { label: 'ATR 14',       color: '#dfe6e9', pane: 'osc',    description: 'Average True Range' },
  adx14:        { label: 'ADX 14',       color: '#fdcb6e', pane: 'osc',    description: 'Average Directional Index' },
  plusDI:       { label: '+DI',          color: '#00cec9', pane: 'osc',    description: 'Directional Index +' },
  minusDI:      { label: '-DI',          color: '#ff7675', pane: 'osc',    description: 'Directional Index -' },
  willr:        { label: 'Williams %R',  color: '#fab1a0', pane: 'osc',    description: 'Williams Percent Range' },
  cci20:        { label: 'CCI 20',       color: '#55efc4', pane: 'osc',    description: 'Commodity Channel Index' },
};

const SIGNAL_COLORS = { BUY: '#00b894', SELL: '#ff7675', NEUTRAL: '#636e72' };

// ─── Hook: fetch chart data ─────────────────────────────────────────────────
function useChartData(symbol: string, timeframe: Timeframe) {
  const [data, setData] = useState<ChartResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (sym: string, tf: Timeframe) => {
    if (!sym) return;
    setLoading(true); setError(null);
    try {
      const res = await api.get<ChartResponse>(
        `/charts/${sym.toUpperCase()}/bars?limit=2000&timeframe=${tf}`
      );
      setData(res);
    } catch (err: any) {
      setError(err.message || "Failed to load chart data");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (symbol) fetchData(symbol, timeframe); }, [symbol, timeframe, fetchData]);

  return { data, loading, error, refetch: () => fetchData(symbol, timeframe) };
}

// ─── Hook: manage chart series ───────────────────────────────────────────────
function useChart(containerEl: HTMLDivElement | null, data: ChartResponse | null, activeIndicators: Set<string>, livePrice: number | null) {
  const chartApiRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<Map<string, any>>(new Map());
  const livePriceRef = useRef<number | null>(null);
  livePriceRef.current = livePrice;

  useEffect(() => {
    if (!containerEl) return;

    // Cleanup previous
    chartApiRef.current?.remove();
    chartApiRef.current = null;
    seriesRef.current.clear();

    const chart = createChart(containerEl, {
      layout: { background: { type: ColorType.Solid, color: '#1a1a2e' }, textColor: '#a0a0b0' },
      grid: { vertLines: { color: '#2a2a3e' }, horzLines: { color: '#2a2a3e' } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#3a3a4e' },
      timeScale: { borderColor: '#3a3a4e', timeVisible: true, secondsVisible: false },
      width: containerEl.clientWidth, height: 420,
    });
    chartApiRef.current = chart;

    const addLine = (key: string, opts: any, paneData: LineData[], scaleId?: string) => {
      if (!paneData || paneData.length === 0) return;
      const s = chart.addSeries(LineSeries, { priceLineVisible: false, lastValueVisible: false, ...opts });
      s.setData(paneData);
      if (scaleId) chart.priceScale(scaleId).applyOptions({ scaleMargins: { top: 0.8, bottom: 0 } });
      seriesRef.current.set(key, s);
    };

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor: '#6c5ce7', downColor: '#ff5252',
      borderDownColor: '#ff5252', borderUpColor: '#6c5ce7',
      wickDownColor: '#ff5252', wickUpColor: '#6c5ce7',
    });
    seriesRef.current.set('_candles', candleSeries);

    if (data?.candles && data.candles.length > 0) {
      // Take only last N candles for rendering performance (lightweight-charts limit)
      const MAX_VISIBLE = 2000;
      const visibleCandles = data.candles.length > MAX_VISIBLE
        ? data.candles.slice(-MAX_VISIBLE)
        : data.candles;
      const cutoffTime = visibleCandles[0]?.time;

      candleSeries.setData(visibleCandles);

      // Helper to filter indicator data to visible range
      const filterVisible = (arr: LineData[]) => {
        if (!arr || arr.length === 0) return [];
        return arr.filter((d: any) => {
          const t = d.time;
          if (typeof t === 'number') return t >= (typeof cutoffTime === 'number' ? cutoffTime : 0);
          if (typeof t === 'object' && t.year) {
            const ct = cutoffTime as any;
            if (!ct?.year) return true;
            return (t.year > ct.year) || (t.year === ct.year && t.month > ct.month) || (t.year === ct.year && t.month === ct.month && t.day >= ct.day);
          }
          return true;
        });
      };

      if (activeIndicators.has('sma20')) addLine('sma20', { color: INDICATORS.sma20.color, lineWidth: 1 }, filterVisible(data.indicators.sma20));
      if (activeIndicators.has('sma50')) addLine('sma50', { color: INDICATORS.sma50.color, lineWidth: 1 }, filterVisible(data.indicators.sma50));
      if (activeIndicators.has('ema12')) addLine('ema12', { color: INDICATORS.ema12.color, lineWidth: 1 }, filterVisible(data.indicators.ema12));
      if (activeIndicators.has('ema26')) addLine('ema26', { color: INDICATORS.ema26.color, lineWidth: 1 }, filterVisible(data.indicators.ema26));
      if (activeIndicators.has('bb_upper') || activeIndicators.has('bb_middle') || activeIndicators.has('bb_lower')) {
        if (activeIndicators.has('bb_upper')) addLine('bb_upper', { color: INDICATORS.bb_upper.color, lineWidth: 1, lineStyle: LineStyle.Dashed }, filterVisible(data.indicators.bb_upper));
        if (activeIndicators.has('bb_middle')) addLine('bb_middle', { color: INDICATORS.bb_middle.color, lineWidth: 1 }, filterVisible(data.indicators.bb_middle));
        if (activeIndicators.has('bb_lower')) addLine('bb_lower', { color: INDICATORS.bb_lower.color, lineWidth: 1, lineStyle: LineStyle.Dashed }, filterVisible(data.indicators.bb_lower));
      }
      if (activeIndicators.has('vwap')) addLine('vwap', { color: INDICATORS.vwap.color, lineWidth: 1 }, filterVisible(data.indicators.vwap));

      if (data.volumes?.length) {
        const volSeries = chart.addSeries(HistogramSeries, { priceFormat: { type: 'volume' }, priceScaleId: 'vol' });
        chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.88, bottom: 0 } });
        volSeries.setData(filterVisible(data.volumes) as any);
        seriesRef.current.set('_volumes', volSeries);
      }

      if (livePriceRef.current) {
        const last = data.candles[data.candles.length - 1];
        candleSeries.update({ ...last, close: livePriceRef.current } as any);
      }
    }

    const onResize = () => chart.applyOptions({ width: containerEl.clientWidth });
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      chart.remove();
      chartApiRef.current = null;
    };
  }, [containerEl, data, activeIndicators, livePrice]);

  return { chartApiRef };
}

// ─── Hook: oscillator pane ────────────────────────────────────────────────────
function useOscillatorChart(container: HTMLDivElement | null, data: ChartResponse | null, activeIndicators: Set<string>) {
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<Map<string, any>>(new Map());

  useEffect(() => {
    if (!container) return;

    chartRef.current?.remove();
    chartRef.current = null;
    seriesRef.current.clear();

    const oscKeys = Object.entries(INDICATORS).filter(([, v]) => v.pane === 'osc').map(([k]) => k);
    const activeOsc = oscKeys.filter(k => activeIndicators.has(k));
    if (activeOsc.length === 0 || !data) return;

    const chart = createChart(container, {
      layout: { background: { type: ColorType.Solid, color: '#1a1a2e' }, textColor: '#a0a0b0' },
      grid: { vertLines: { color: '#2a2a3e' }, horzLines: { color: '#2a2a4e' } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: '#3a3a4e' },
      timeScale: { borderColor: '#3a3a4e', timeVisible: true, secondsVisible: false },
      width: container.clientWidth, height: 180,
    });
    chartRef.current = chart;

    const addLine = (key: string, opts: any, paneData: LineData[]) => {
      if (!paneData || paneData.length === 0) return;
      const sliced = paneData.length > 2000 ? paneData.slice(-2000) : paneData;
      const s = chart.addSeries(LineSeries, { priceLineVisible: false, lastValueVisible: false, ...opts });
      s.setData(sliced);
      seriesRef.current.set(key, s);
    };
    const addHist = (key: string, paneData: HistData[]) => {
      if (!paneData || paneData.length === 0) return;
      const sliced = paneData.length > 2000 ? paneData.slice(-2000) : paneData;
      const s = chart.addSeries(HistogramSeries, { priceLineVisible: false });
      s.setData(sliced);
      seriesRef.current.set(key, s);
    };

    const ind = data.indicators as Indicators;
    if (activeOsc.includes('rsi14')) addLine('rsi14', { color: INDICATORS.rsi14.color, lineWidth: 2 }, ind.rsi14);
    if (activeOsc.includes('macd_line')) addLine('macd_line', { color: INDICATORS.macd_line.color, lineWidth: 2 }, ind.macd_line);
    if (activeOsc.includes('macd_signal')) addLine('macd_signal', { color: INDICATORS.macd_signal.color, lineWidth: 1 }, ind.macd_signal);
    if (activeOsc.includes('macd_histogram')) addHist('macd_histogram', ind.macd_histogram);
    if (activeOsc.includes('stoch_k')) addLine('stoch_k', { color: INDICATORS.stoch_k.color, lineWidth: 1 }, ind.stoch_k);
    if (activeOsc.includes('stoch_d')) addLine('stoch_d', { color: INDICATORS.stoch_d.color, lineWidth: 1 }, ind.stoch_d);
    if (activeOsc.includes('atr14')) addLine('atr14', { color: INDICATORS.atr14.color, lineWidth: 1 }, ind.atr14);
    if (activeOsc.includes('adx14')) addLine('adx14', { color: INDICATORS.adx14.color, lineWidth: 2 }, ind.adx14);
    if (activeOsc.includes('plusDI')) addLine('plusDI', { color: INDICATORS.plusDI.color, lineWidth: 1 }, ind.plusDI);
    if (activeOsc.includes('minusDI')) addLine('minusDI', { color: INDICATORS.minusDI.color, lineWidth: 1 }, ind.minusDI);
    if (activeOsc.includes('willr')) addLine('willr', { color: INDICATORS.willr.color, lineWidth: 1 }, ind.willr);
    if (activeOsc.includes('cci20')) addLine('cci20', { color: INDICATORS.cci20.color, lineWidth: 1 }, ind.cci20);

    const onResize = () => chart.applyOptions({ width: container.clientWidth });
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); chart.remove(); chartRef.current = null; };
  }, [container, data, activeIndicators]);
}

// ─── Indicator Toggle Button ─────────────────────────────────────────────────
function IndBtn({ name, active, onToggle }: { name: string; active: boolean; onToggle: () => void }) {
  const cfg = INDICATORS[name];
  if (!cfg) return null;
  return (
    <button
      onClick={onToggle}
      title={cfg.description}
      style={{
        background: active ? cfg.color + '30' : 'transparent',
        color: active ? cfg.color : '#666',
        border: `1px solid ${active ? cfg.color + '60' : '#3a3a4e'}`,
        borderRadius: 4,
        padding: '3px 8px',
        fontSize: 11,
        cursor: 'pointer',
        fontFamily: 'inherit',
        transition: 'all 0.15s',
      }}
    >
      {cfg.label}
    </button>
  );
}

// ─── Signal Badge ─────────────────────────────────────────────────────────────
function SignalBadge({ s }: { s: Signal }) {
  const color = SIGNAL_COLORS[s.type];
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: color + '15', border: `1px solid ${color}40`,
      borderRadius: 6, padding: '6px 10px', flex: '1 1 200px',
    }}>
      <span style={{
        background: color, color: '#fff', borderRadius: 4,
        padding: '2px 6px', fontSize: 10, fontWeight: 700,
      }}>{s.type}</span>
      <span style={{ fontSize: 11, color: '#ccc' }}>{s.indicator}</span>
      <span style={{ fontSize: 11, color: '#888', flex: 1 }}>{s.reason}</span>
      <span style={{ fontSize: 10, color: '#666' }}>{'★'.repeat(s.strength)}</span>
      <span style={{ fontSize: 10, color: '#555' }}>{s.date}</span>
    </div>
  );
}

// ─── Pattern Card ────────────────────────────────────────────────────────────
function PatternCard({ p }: { p: Pattern }) {
  const trendColor = p.trend === 'BULLISH' ? '#00b894' : p.trend === 'BEARISH' ? '#ff7675' : '#636e72';
  return (
    <div style={{
      background: '#22223a', border: '1px solid #3a3a4e', borderRadius: 8, padding: 12,
    }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <span style={{ fontSize: 11, color: '#666' }}>Trend </span>
          <span style={{ color: trendColor, fontWeight: 700, fontSize: 13 }}>{p.trend}</span>
        </div>
        <div>
          <span style={{ fontSize: 11, color: '#666' }}>Pattern </span>
          <span style={{ color: '#e0e0e0', fontWeight: 600, fontSize: 13 }}>{p.pattern}</span>
          {p.confidence > 0 && (
            <span style={{ color: p.confidence > 65 ? '#00b894' : '#fdcb6e', fontSize: 11, marginLeft: 6 }}>
              {p.confidence}% confidence
            </span>
          )}
        </div>
        <div>
          <span style={{ fontSize: 11, color: '#666' }}>Support </span>
          <span style={{ color: '#00cec9', fontWeight: 700 }}>{p.support?.toFixed(2)}</span>
        </div>
        <div>
          <span style={{ fontSize: 11, color: '#666' }}>Resistance </span>
          <span style={{ color: '#ff7675', fontWeight: 700 }}>{p.resistance?.toFixed(2)}</span>
        </div>
      </div>
      {p.candlePattern && (
        <div style={{ marginTop: 6, fontSize: 11, color: '#a29bfe' }}>
          Candlestick: {p.candlePattern}
        </div>
      )}
    </div>
  );
}

// ─── Empty Chart Placeholder ─────────────────────────────────────────────────
function EmptyChart({ symbol }: { symbol: string }) {
  return (
    <div style={{
      height: 420, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 12,
      background: '#1a1a2e', borderRadius: 8, border: '1px solid #3a3a4e',
    }}>
      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3a3a4e" strokeWidth="1.5">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 16, color: '#888' }}>No historical data for <strong style={{ color: '#e0e0e0' }}>{symbol}</strong></p>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#555' }}>Import EOD bars via the fetch script or check the symbol name</p>
      </div>
      <a href="/universe" style={{
        background: '#6c5ce7', color: '#fff', textDecoration: 'none',
        borderRadius: 6, padding: '8px 16px', fontSize: 13,
      }}>Browse Universe</a>
    </div>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────
function StatsBar({ data }: { data: ChartResponse }) {
  const trendColor = data.pattern?.trend === 'BULLISH' ? '#00b894' : data.pattern?.trend === 'BEARISH' ? '#ff7675' : '#636e72';
  return (
    <div style={{
      display: 'flex', gap: 16, flexWrap: 'wrap',
      padding: '8px 12px', background: '#22223a', borderRadius: 8,
      fontSize: 12, color: '#a0a0b0', marginBottom: 8,
    }}>
      <span style={{ color: '#e0e0e0', fontWeight: 600 }}>{data.symbol}</span>
      <span>Bars: <strong style={{ color: '#ccc' }}>{data.barCount}</strong></span>
      {data.dateRange?.from && <span>From: <strong>{String(data.dateRange.from).slice(0,10)}</strong></span>}
      {data.dateRange?.to && <span>To: <strong>{String(data.dateRange.to).slice(0,10)}</strong></span>}
      {data.livePrice && (
        <span style={{ color: '#6c5ce7', fontWeight: 700 }}>
          Live: Rs{data.livePrice.toFixed(2)}
        </span>
      )}
      {data.pattern && (
        <span style={{ color: trendColor, fontWeight: 600 }}>
          {data.pattern.trend}
        </span>
      )}
      {data.pattern && (
        <span style={{ color: '#a0a0b0' }}>
          S: <strong style={{ color: '#00cec9' }}>{data.pattern.support?.toFixed(1)}</strong>
          {' / '}
          R: <strong style={{ color: '#ff7675' }}>{data.pattern.resistance?.toFixed(1)}</strong>
        </span>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function Charts() {
  const { symbol = '' } = useParams<{ symbol: string }>();
  const navigate = useNavigate();
  const [searchVal, setSearchVal] = useState(symbol);
  const [timeframe, setTimeframe] = useState<Timeframe>('1D');
  const [activeIndicators, setActiveIndicators] = useState<Set<string>>(new Set(['sma20', 'sma50']));
  const [panelOpen, setPanelOpen] = useState(true);

  const [mainContainer, setMainContainer] = useState<HTMLDivElement | null>(null);
  const [oscContainer, setOscContainer] = useState<HTMLDivElement | null>(null);

  const { data, loading, error } = useChartData(symbol, timeframe);
  useChart(mainContainer, data, activeIndicators, data?.livePrice ?? null);
  useOscillatorChart(oscContainer, data, activeIndicators);
  const activeOscCount = Object.entries(INDICATORS).filter(([k, v]) => v.pane === 'osc' && activeIndicators.has(k)).length;

  const toggleIndicator = (key: string) => {
    setActiveIndicators(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const sym = searchVal.trim().toUpperCase();
    if (sym) navigate(`/charts/${sym}`);
  };

  // Show/hide oscillator pane based on active oscillators
  const showOsc = data && data.barCount > 0 && activeOscCount > 0;

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, color: '#e0e0e0', fontSize: 20 }}>
          {symbol ? `${symbol} Chart` : 'Trading View'}
        </h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
            <input
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
              placeholder="Symbol (e.g. RELIANCE)"
              style={{
                background: '#2b2b3d', color: '#e0e0e0', border: '1px solid #3a3a4e',
                borderRadius: 6, padding: '6px 12px', fontSize: 13, width: 180,
              }}
            />
            <button type="submit" style={{
              background: '#6c5ce7', color: '#fff', border: 'none', borderRadius: 6,
              padding: '6px 16px', cursor: 'pointer', fontSize: 13,
            }}>Go</button>
          </form>
          {/* Timeframe selector */}
        {symbol && (
          <div style={{ display: 'flex', gap: 4 }}>
            {(['1m', '5m', '1D'] as Timeframe[]).map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                style={{
                  background: timeframe === tf ? '#6c5ce7' : 'transparent',
                  color: timeframe === tf ? '#fff' : '#888',
                  border: `1px solid ${timeframe === tf ? '#6c5ce7' : '#3a3a4e'}`,
                  borderRadius: 4, padding: '4px 12px', cursor: 'pointer',
                  fontSize: 12, fontWeight: timeframe === tf ? 700 : 400,
                  transition: 'all 0.15s',
                }}
              >
                {tf}
              </button>
            ))}
          </div>
        )}

        {/* Indicator toggle */}
        <button
          onClick={() => setPanelOpen(p => !p)}
          style={{
            background: panelOpen ? '#6c5ce720' : '#2b2b3d',
            color: '#a0a0b0', border: '1px solid #3a3a4e',
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12,
          }}
        >
          Indicators {panelOpen ? '▲' : '▼'}
        </button>
        </div>
      </div>

      {/* Indicator Panel */}
      {panelOpen && (
        <div style={{
          marginBottom: 8, padding: '10px 12px', background: '#1e1e32',
          borderRadius: 8, border: '1px solid #2a2a3e',
        }}>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>OVERLAY (same chart)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
            {Object.entries(INDICATORS).filter(([, v]) => v.pane === 'main').map(([k]) => (
              <IndBtn key={k} name={k} active={activeIndicators.has(k)} onToggle={() => toggleIndicator(k)} />
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>OSCILLATORS (separate pane)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {Object.entries(INDICATORS).filter(([, v]) => v.pane === 'osc').map(([k]) => (
              <IndBtn key={k} name={k} active={activeIndicators.has(k)} onToggle={() => toggleIndicator(k)} />
            ))}
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ color: '#a0a0b0', textAlign: 'center', padding: 40 }}>
          Loading {symbol}...
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: '#ff525220', border: '1px solid #ff5252', borderRadius: 8, padding: 16, color: '#ff8a80' }}>
          {error}
        </div>
      )}

      {/* No symbol selected */}
      {!symbol && !loading && (
        <div style={{ textAlign: 'center', color: '#888', padding: 60 }}>
          <p style={{ fontSize: 16 }}>Enter a stock symbol to view the chart</p>
          <p style={{ fontSize: 13 }}>Examples: RELIANCE, TCS, INFY, HDFCBANK, NIFTYBEES</p>
        </div>
      )}

      {/* Stats bar */}
      {data && data.barCount > 0 && <StatsBar data={data} />}

      {/* Main chart area */}
      {data && data.barCount === 0 && !loading && (
        <EmptyChart symbol={data.symbol} />
      )}

      {data && data.barCount > 0 && (
        <>
          {/* Candlestick + overlays */}
          <div ref={el => setMainContainer(el)} style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 4 }} />

          {/* Oscillator pane */}
          {showOsc && (
            <>
              <div ref={el => setOscContainer(el)} style={{ borderRadius: 8, overflow: 'hidden', marginBottom: 4 }} />
              <div style={{ fontSize: 11, color: '#555', textAlign: 'center', marginBottom: 8 }}>
                Oscillators: {Object.entries(INDICATORS).filter(([k, v]) => v.pane === 'osc' && activeIndicators.has(k)).map(([k]) => INDICATORS[k].label).join(' · ')}
              </div>
            </>
          )}

          {/* Signals */}
          {data.signals && data.signals.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>ACTIVE SIGNALS</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.signals.map((s, i) => <SignalBadge key={i} s={s} />)}
              </div>
            </div>
          )}

          {/* Pattern */}
          {data.pattern && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: '#555', marginBottom: 6 }}>PATTERN ANALYSIS</div>
              <PatternCard p={data.pattern} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
